import { useState, useEffect, useRef } from 'react';
import {
  Code2,
  Loader2,
  RefreshCw,
  ExternalLink,
  AlertCircle,
  Server,
  AlertTriangle,
} from 'lucide-react';

// API Base URLs by environment
const API_URLS: Record<string, string> = {
  dev: 'https://api-dev.playtark.com',
  staging: 'https://api-staging.playtark.com',
  prod: 'https://api.playtark.com',
  local: 'http://localhost:8080',
};

export function ApiDocsPage() {
  // State
  const [environment, setEnvironment] = useState<'dev' | 'staging' | 'prod' | 'local'>('dev');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Get swagger URL
  const getSwaggerUrl = () => {
    const baseUrl = API_URLS[environment];
    return `${baseUrl}/swagger-ui/index.html`;
  };

  // Get OpenAPI spec URL
  const getOpenApiUrl = () => {
    const baseUrl = API_URLS[environment];
    return `${baseUrl}/v3/api-docs`;
  };

  // Handle iframe load
  const handleIframeLoad = () => {
    setIsLoading(false);
    setError(null);
  };

  // Handle iframe error
  const handleIframeError = () => {
    setIsLoading(false);
    setError('Failed to load Swagger UI. The API server may be unavailable.');
  };

  // Reload iframe when environment changes
  useEffect(() => {
    setIsLoading(true);
    setError(null);
  }, [environment]);

  return (
    <div className="flex flex-col min-h-0 h-full">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Code2 className="h-7 w-7 text-cyan-400" />
            API Documentation
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            Interactive Swagger UI for TARK API
          </p>
        </div>

        <div className="flex items-center gap-4">
          {/* Environment selector */}
          <div className="flex items-center gap-2">
            <Server className="h-4 w-4 text-slate-400" />
            <select
              value={environment}
              onChange={(e) => setEnvironment(e.target.value as 'dev' | 'staging' | 'prod' | 'local')}
              className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
            >
              <option value="local">Local (localhost:8080)</option>
              <option value="dev">Development</option>
              <option value="staging">Staging</option>
              <option value="prod">Production</option>
            </select>
          </div>

          {/* Open in new tab - RECOMMENDED approach for cookie-based auth */}
          <a
            href={getSwaggerUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-colors font-medium"
          >
            <ExternalLink className="h-4 w-4" />
            Open Swagger UI
          </a>

          {/* Refresh */}
          <button
            onClick={() => {
              setIsLoading(true);
              iframeRef.current?.contentWindow?.location.reload();
            }}
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 rounded-lg transition-colors"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh
          </button>
        </div>
      </div>

      {/* Cookie-based auth notice */}
      <div className="mb-4 p-3 bg-amber-900/20 border border-amber-700/50 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-200 font-medium">
              For best results, open Swagger UI in a new tab
            </p>
            <p className="mt-1 text-xs text-amber-300/70">
              The admin app uses cookie-based authentication (BFF pattern). Opening Swagger UI directly
              at the API domain ensures your session cookies are properly sent with API requests.
              The iframe preview below may have limited functionality due to cross-origin restrictions.
            </p>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="mb-4 flex items-center gap-4">
        <span className="text-sm text-slate-500">Quick Links:</span>
        <a
          href={getOpenApiUrl()}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-cyan-400 hover:text-cyan-300"
        >
          OpenAPI JSON
        </a>
        <span className="text-slate-600">|</span>
        <a
          href={`${API_URLS[environment]}/actuator/health`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-cyan-400 hover:text-cyan-300"
        >
          Health Check
        </a>
        <span className="text-slate-600">|</span>
        <a
          href={`${API_URLS[environment]}/actuator/info`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-cyan-400 hover:text-cyan-300"
        >
          API Info
        </a>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-4 flex items-center gap-3 p-4 bg-red-900/20 border border-red-800/50 rounded-lg text-red-400">
          <AlertCircle className="h-5 w-5" />
          {error}
        </div>
      )}

      {/* Swagger iframe preview */}
      <div className="flex-1 rounded-lg border border-slate-700 overflow-hidden relative">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80 z-10">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
              <span className="text-slate-400">Loading Swagger UI preview...</span>
            </div>
          </div>
        )}

        <iframe
          ref={iframeRef}
          src={getSwaggerUrl()}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          className="w-full h-full bg-white"
          title="Swagger UI"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
        />
      </div>

      {/* Footer info */}
      <div className="mt-2 text-xs text-slate-500 flex items-center justify-between">
        <span>
          Currently viewing: <code className="text-cyan-400">{API_URLS[environment]}</code>
        </span>
        <span>
          Tip: For authenticated API calls, use the "Open Swagger UI" button above
        </span>
      </div>
    </div>
  );
}
